"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { handleApiErrorResponse } from "@/lib/apiErrorHandler";

export type SubscriptionData = {
  tier: string;
  isTrial: boolean;
  trialDaysRemaining: number;
  stripeSubscriptionId: string;
  status: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
};

export type SubscriptionPlan = {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  description: string;
};

export type Invoice = {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  invoicePdfUrl: string;
};

export const currentSubscriptionQueryKey = ["currentSubscription"];
export const subscriptionPlansQueryKey = ["subscriptionPlans"];
export const billingHistoryQueryKey = ["billingHistory"];

/**
 * Hook to get current subscription status details
 */
export function useCurrentSubscriptionQuery() {
  const { status } = useSession();

  return useQuery({
    queryKey: currentSubscriptionQueryKey,
    queryFn: async (): Promise<SubscriptionData> => {
      const response = await fetch("/api/v1/subscriptions/current");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
    enabled: status === "authenticated",
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get available subscription plans
 */
export function useSubscriptionPlansQuery() {
  const { status } = useSession();

  return useQuery({
    queryKey: subscriptionPlansQueryKey,
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const response = await fetch("/api/v1/subscriptions/plans");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
    enabled: status === "authenticated",
    staleTime: 1000 * 60 * 60, // 1 hour (plans rarely change)
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get user billing/invoice history
 */
export function useBillingHistoryQuery() {
  const { status } = useSession();

  return useQuery({
    queryKey: billingHistoryQueryKey,
    queryFn: async (): Promise<Invoice[]> => {
      const response = await fetch("/api/v1/subscriptions/billing-history");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
    enabled: status === "authenticated",
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to cancel the current subscription (scheduled at period end)
 */
export function useCancelSubscriptionMutation() {
  const queryClient = useQueryClient();
  const { update: updateSession } = useSession();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/v1/subscriptions/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      return response.json();
    },
    onSuccess: async (data) => {
      // Invalidate current subscription query
      await queryClient.invalidateQueries({ queryKey: currentSubscriptionQueryKey });
      
      // Update session status to match
      const newStatus = data?.data?.status ?? "CANCELED_AT_PERIOD_END";
      await updateSession({
        user: {
          subscriptionStatus: newStatus,
          hasSubscription: true, // remains true until the end of the billing period
        },
      });
    },
  });
}

/**
 * Hook to reactivate a subscription scheduled for cancellation
 */
export function useReactivateSubscriptionMutation() {
  const queryClient = useQueryClient();
  const { update: updateSession } = useSession();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/v1/subscriptions/reactivate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      return response.json();
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: currentSubscriptionQueryKey });

      const newStatus = data?.data?.status ?? "ACTIVE";
      await updateSession({
        user: {
          subscriptionStatus: newStatus,
          hasSubscription: true,
        },
      });
    },
  });
}

/**
 * Hook to generate Stripe Checkout URL and start payment process
 */
export function useCheckoutMutation() {
  return useMutation({
    mutationFn: async (): Promise<{ url: string }> => {
      const response = await fetch("/api/v1/subscriptions/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}), // empty body per API spec
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}
